/**
 * Appointment timing helpers.
 *
 * Ayden / ECW return appointment times as wall-clock strings like
 * "2026-03-18 15:15:00" with NO timezone offset. Those strings are in the
 * facility's local time, which may differ from the device's local time
 * (traveling provider, telehealth, remote admin user, etc).
 *
 * We always interpret them in the practice timezone read from
 * `EhrConfig.timeZone` (falling back to `America/New_York` for legacy
 * configs — see `ehrService.resolveTimeZone`).
 *
 * The functions here are pure: pass in `now` and `timeZone` so they're
 * trivial to test and don't depend on `new Date()` at call time.
 */

// Default window when Ayden didn't give us an explicit `end`. ECW visits
// are typically 15–30 min — 30 min is the safe over-estimate that keeps
// the "Now" badge visible for a reasonable length of time.
const DEFAULT_APPOINTMENT_DURATION_MINUTES = 30;

// How far into the future counts as "starting soon". Matches the
// push-notification window most EHRs use for reminders.
const SOON_WINDOW_MINUTES = 30;

// Appointments whose end is this-many-minutes past count as "just ended"
// and still pin to the top briefly so the provider can finish a note.
const JUST_ENDED_GRACE_MINUTES = 15;

// Statuses from Ayden that should be ignored entirely (no badge, no pin).
// Everything else falls through to the timing-based classification.
const IGNORED_STATUSES = new Set([
  'cancelled',
  'canceled',
  'no-show',
  'noshow',
  'no_show',
  'rescheduled',
]);

export interface AppointmentLike {
  appointmentDate?: string | null;
  appointmentEnd?: string | null;
  appointmentTime?: string | null;
  status?: string | null;
}

export type AppointmentKind =
  | 'now'
  | 'soon'
  | 'justEnded'
  | 'laterToday'
  | 'past'
  | 'future'
  | 'unknown';

export interface AppointmentStatus {
  kind: AppointmentKind;
  start: Date | null;
  end: Date | null;
  minutesUntilStart: number | null;
  minutesSinceEnd: number | null;
}

/**
 * Convert a facility wall-clock string ("YYYY-MM-DD HH:MM:SS" or
 * "YYYY-MM-DD" or ISO) + an IANA timezone id → an absolute UTC Date.
 *
 * We purposefully do NOT use `Date.parse` on raw wall-clock strings: V8
 * interprets "2026-03-18 15:15:00" as local-device time, which is exactly
 * the bug we're trying to avoid. Instead we compute the TZ's current UTC
 * offset for that wall-clock and subtract it.
 *
 * Returns null for anything unparseable.
 */
export function wallClockInTimeZoneToDate(
  input: unknown,
  timeZone: string | undefined,
): Date | null {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // If the caller already gave us an ISO with offset/Z, just parse normally.
  if (/[TZ]|[+-]\d\d:?\d\d$/.test(trimmed)) {
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  // Accept either "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DD HH:MM" or bare date.
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (!match) return null;

  const [, y, mo, d, h = '00', mi = '00', s = '00'] = match;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(h);
  const minute = Number(mi);
  const second = Number(s);

  // Start with the naive UTC instant for those components.
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  // Ask `Intl` what wall-clock that naive UTC would show as, in the target
  // timezone. The difference gives us the offset for that instant.
  const offsetMs = getTimeZoneOffsetMs(naiveUtc, timeZone);

  return new Date(naiveUtc - offsetMs);
}

/**
 * Compute the UTC offset in ms for `instantMs` when rendered in `timeZone`.
 * Uses `Intl.DateTimeFormat` so DST transitions are handled correctly for
 * every day of the year.
 */
function getTimeZoneOffsetMs(
  instantMs: number,
  timeZone: string | undefined,
): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instantMs));

  const pick = (type: string) =>
    Number(parts.find(p => p.type === type)?.value ?? '0');
  let hour = pick('hour');
  // `hour: '2-digit'` returns "24" at midnight in some locales.
  if (hour === 24) hour = 0;

  const asUtc = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    hour,
    pick('minute'),
    pick('second'),
  );
  return asUtc - instantMs;
}

/**
 * Classify an appointment relative to `now`.
 *
 * @param appt - { appointmentDate, appointmentEnd?, appointmentTime?, status? }
 *   `appointmentDate` may be "YYYY-MM-DD HH:MM:SS" OR "YYYY-MM-DD" plus a
 *   separate `appointmentTime`.
 * @param now - current instant (usually `new Date()`, injectable for tests)
 * @param timeZone - IANA timezone id for the facility
 */
export function computeAppointmentStatus(
  appt: AppointmentLike | null | undefined,
  now: Date,
  timeZone: string | undefined,
): AppointmentStatus {
  if (!appt) return emptyStatus();

  const status =
    typeof appt.status === 'string' ? appt.status.toLowerCase() : '';
  if (IGNORED_STATUSES.has(status)) return emptyStatus();

  const dateStr = normalizeStartString(appt);
  const start = wallClockInTimeZoneToDate(dateStr, timeZone);
  if (!start) return emptyStatus();

  const endStr =
    typeof appt.appointmentEnd === 'string' ? appt.appointmentEnd : null;
  const endExplicit = endStr
    ? wallClockInTimeZoneToDate(endStr, timeZone)
    : null;
  const end =
    endExplicit ??
    new Date(start.getTime() + DEFAULT_APPOINTMENT_DURATION_MINUTES * 60_000);

  const msUntilStart = start.getTime() - now.getTime();
  const msSinceEnd = now.getTime() - end.getTime();

  const minutesUntilStart = Math.round(msUntilStart / 60_000);
  const minutesSinceEnd = Math.round(msSinceEnd / 60_000);

  let kind: AppointmentKind = 'future';
  if (now >= start && now < end) {
    kind = 'now';
  } else if (msUntilStart > 0 && msUntilStart <= SOON_WINDOW_MINUTES * 60_000) {
    kind = 'soon';
  } else if (
    msSinceEnd >= 0 &&
    msSinceEnd <= JUST_ENDED_GRACE_MINUTES * 60_000
  ) {
    // Note: `>=` not `>` so the instant `now === end` classifies as
    // just-ended rather than falling through to "laterToday".
    kind = 'justEnded';
  } else if (msSinceEnd > JUST_ENDED_GRACE_MINUTES * 60_000) {
    kind = 'past';
  } else if (isSameFacilityDay(start, now, timeZone)) {
    kind = 'laterToday';
  }

  return {kind, start, end, minutesUntilStart, minutesSinceEnd};
}

function emptyStatus(): AppointmentStatus {
  return {
    kind: 'unknown',
    start: null,
    end: null,
    minutesUntilStart: null,
    minutesSinceEnd: null,
  };
}

function normalizeStartString(appt: AppointmentLike): string | null {
  const raw = appt.appointmentDate;
  if (!raw || typeof raw !== 'string') return null;
  // If raw already contains a time component, use as-is; otherwise splice
  // appointmentTime in.
  if (/[T ]\d{2}:\d{2}/.test(raw)) return raw;
  const time =
    typeof appt.appointmentTime === 'string' && appt.appointmentTime
      ? appt.appointmentTime
      : '00:00:00';
  return `${raw} ${time}`;
}

function isSameFacilityDay(
  a: Date,
  b: Date,
  timeZone: string | undefined,
): boolean {
  const fmt = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  return fmt(a) === fmt(b);
}

/**
 * Sort key used by the patient list. Lower = appears first. "Now" pins to
 * the very top, then "soon" (sorted by start), then "justEnded", then
 * "laterToday" (by start), then everything else gets `Number.POSITIVE_INFINITY`
 * so the caller can fall back to their existing sort (e.g. alphabetical).
 */
export function appointmentSortKey(
  status: AppointmentStatus | null | undefined,
): number {
  if (!status) return Number.POSITIVE_INFINITY;
  switch (status.kind) {
    case 'now':
      // All "now" rows tie at the top; break ties by how recently they started.
      return -1_000_000_000 + (status.start?.getTime() ?? 0);
    case 'soon':
      // Earlier = higher priority.
      return status.start?.getTime() ?? 0;
    case 'justEnded':
      return 9_000_000_000_000; // after every "soon" but before "laterToday"
    case 'laterToday':
      return 10_000_000_000_000 + (status.start?.getTime() ?? 0);
    default:
      return Number.POSITIVE_INFINITY;
  }
}

/**
 * Facility-aware wall-clock formatter for the UI badge.
 * Returns e.g. "2:30 PM" in the facility's timezone.
 */
export function formatFacilityTime(
  date: Date | null | undefined,
  timeZone: string | undefined,
): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}
